import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, Printer, Edit3, CheckCircle2, XCircle, Copy,
  CreditCard, X, RefreshCw, AlertCircle, ChevronRight
} from 'lucide-react';
import billingService from '../../../services/billingService';
import toast from 'react-hot-toast';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (n) => (n ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const DOC_TITLES = {
  sales: 'TAX INVOICE', purchase: 'PURCHASE BILL', sales_return: 'CREDIT NOTE',
  purchase_return: 'DEBIT NOTE', credit_note: 'CREDIT NOTE', debit_note: 'DEBIT NOTE',
  quotation: 'QUOTATION', proforma: 'PROFORMA INVOICE', delivery_challan: 'DELIVERY CHALLAN',
  receipt: 'RECEIPT VOUCHER', payment: 'PAYMENT VOUCHER', journal: 'JOURNAL ENTRY'
};

function numberToWords(num) {
  const a = ['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine',
    'Ten','Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen','Seventeen','Eighteen','Nineteen'];
  const b = ['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];
  const inW = (n) => n > 19 ? b[Math.floor(n/10)] + ' ' + a[n%10] : a[n];
  const amount = Math.floor(num || 0);
  if (amount === 0) return 'Zero';
  let w = '';
  if (Math.floor(amount/10000000)) w += inW(Math.floor(amount/10000000)) + ' Crore ';
  if (Math.floor((amount%10000000)/100000)) w += inW(Math.floor((amount%10000000)/100000)) + ' Lakh ';
  if (Math.floor((amount%100000)/1000)) w += inW(Math.floor((amount%100000)/1000)) + ' Thousand ';
  if (Math.floor((amount%1000)/100)) w += a[Math.floor((amount%1000)/100)] + ' Hundred ';
  if (amount%100) w += inW(amount%100);
  return 'Rupees ' + w.trim() + ' Only';
}

// ── Payment Modal ─────────────────────────────────────────────────────────────
function PaymentModal({ billing, onSave, onClose }) {
  const remaining = Math.max(0, billing.totalAmount - (billing.paidAmount || 0));
  const [form, setForm] = useState({ paidAmount: remaining, mode: 'bank_transfer', paymentDate: new Date().toISOString().split('T')[0], reference: '', bank: '' });
  const [saving, setSaving] = useState(false);
  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await billingService.updatePayment(billing._id, form);
      toast.success('Payment recorded');
      onSave();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-slate-900 rounded-t-2xl">
          <h3 className="font-bold text-sm text-white flex items-center gap-2"><CreditCard className="w-4 h-4 text-emerald-400" /> Record Payment</h3>
          <button onClick={onClose} className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSave} className="p-5 space-y-3">
          <div><label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Amount (₹) *</label>
            <input type="number" step="0.01" min="0.01" max={remaining > 0 ? remaining : billing.totalAmount}
              value={form.paidAmount} onChange={e => setForm(f => ({ ...f, paidAmount: e.target.value }))} required
              className="w-full px-3 py-2.5 text-sm font-mono font-bold rounded-xl border border-slate-200 bg-slate-50 outline-none focus:border-purple-400" />
          </div>
          <div><label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Payment Mode *</label>
            <select value={form.mode} onChange={e => setForm(f => ({ ...f, mode: e.target.value }))}
              className="w-full px-3 py-2.5 text-xs font-semibold rounded-xl border border-slate-200 bg-slate-50">
              <option value="bank_transfer">Bank Transfer / NEFT / RTGS</option>
              <option value="upi">UPI</option><option value="cash">Cash</option>
              <option value="card">Card</option><option value="cheque">Cheque</option>
              <option value="credit">On Credit</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Date</label>
              <input type="date" value={form.paymentDate} onChange={e => setForm(f => ({ ...f, paymentDate: e.target.value }))}
                className="w-full px-3 py-2 text-xs font-semibold rounded-xl border border-slate-200 bg-slate-50 outline-none" />
            </div>
            <div><label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Ref / UTR / Cheque</label>
              <input value={form.reference} onChange={e => setForm(f => ({ ...f, reference: e.target.value }))} placeholder="Reference"
                className="w-full px-3 py-2 text-xs font-semibold rounded-xl border border-slate-200 bg-slate-50 outline-none" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-xs font-bold rounded-xl border border-slate-200 text-slate-600">Cancel</button>
            <button type="submit" disabled={saving} className="px-5 py-2 text-xs font-bold rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-60">
              {saving ? 'Saving…' : 'Record Payment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main InvoiceDetail ────────────────────────────────────────────────────────
export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPayment, setShowPayment] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const printRef = useRef(null);
  const logoInputRef = useRef(null);
  const [logoUrl, setLogoUrl] = useState(() => localStorage.getItem('invoiceLogoUrl') || null);
  const [logoUploading, setLogoUploading] = useState(false);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Select an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Logo must be under 5 MB'); return; }
    try {
      setLogoUploading(true);
      toast.loading('Uploading logo...', { id: 'logo-upload' });
      const formData = new FormData();
      formData.append('logo', file);
      const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const token = localStorage.getItem('token');
      const res = await fetch(`${apiBase}/billing/upload-logo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.message || 'Upload failed'); }
      const json = await res.json();
      localStorage.setItem('invoiceLogoUrl', json.logoUrl);
      setLogoUrl(json.logoUrl);
      toast.success('Logo uploaded!', { id: 'logo-upload' });
    } catch (err) {
      toast.error(err.message || 'Logo upload failed', { id: 'logo-upload' });
    } finally {
      setLogoUploading(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const handleLogoRemove = () => {
    localStorage.removeItem('invoiceLogoUrl');
    setLogoUrl(null);
    if (logoInputRef.current) logoInputRef.current.value = '';
    toast.success('Logo removed');
  };

  const handlePrint = async () => {
    const element = printRef.current;
    if (!element) return;
    try {
      setGeneratingPdf(true);
      toast.loading('Generating PDF...', { id: 'pdf' });
      const canvas = await html2canvas(element, { scale: 2, useCORS: true, logging: false, backgroundColor: '#ffffff' });
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();
      const ih = (canvas.height * pw) / canvas.width;
      let rem = ih;
      while (rem > 0) {
        pdf.addImage(imgData, 'JPEG', 0, -(ih - rem), pw, ih);
        rem -= ph;
        if (rem > 0) pdf.addPage();
      }
      pdf.save(`Invoice-${billing?.billNumber || 'document'}.pdf`);
      toast.success('PDF downloaded!', { id: 'pdf' });
    } catch (err) {
      toast.error('PDF failed. Try again.', { id: 'pdf' });
    } finally {
      setGeneratingPdf(false);
    }
  };

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await billingService.getBillingById(id);
      setBilling(res.data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, [id]);

  const handleApprove = async () => {
    if (!window.confirm('Approve this document?')) return;
    await billingService.approveBilling(id);
    toast.success('Approved'); fetch();
  };

  const handleCancel = async () => {
    const reason = window.prompt('Cancellation reason (optional):');
    if (reason === null) return;
    try { await billingService.cancelBilling(id, { reason }); toast.success('Cancelled'); fetch(); }
    catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const handleDuplicate = async () => {
    try {
      const res = await billingService.duplicateBilling(id);
      toast.success(`Duplicated → ${res.data.billNumber}`);
      navigate(`/admin/billing/${res.data._id}/edit`);
    } catch (err) { toast.error(err.message); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-4 border-purple-600 border-t-transparent" /></div>;
  if (!billing) return <div className="p-12 text-center"><p className="text-red-600 font-bold">Document not found.</p></div>;

  const balance = Math.max(0, billing.totalAmount - (billing.paidAmount || 0));
  const docTitle = DOC_TITLES[billing.billingType] || 'DOCUMENT';
  const partyName = billing.partyName || billing.party?.name || billing.customer?.name || billing.vendor?.name || '—';
  const partyGstin = billing.partyGstin || billing.party?.gstin || billing.customerGstin || billing.vendorGstin || '';

  return (
    <div className="max-w-5xl mx-auto space-y-4 pb-10">
      {/* Action Bar */}
      <div className="print:hidden flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white border border-slate-200 rounded-2xl px-5 py-3">
        <button onClick={() => navigate('/admin/billing')}
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => setShowPayment(true)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs">
            <CreditCard className="w-3.5 h-3.5" /> Record Payment
          </button>
          {billing.status !== 'accepted' && billing.status !== 'cancelled' && (
            <button onClick={handleApprove}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs">
              <CheckCircle2 className="w-3.5 h-3.5" /> Approve
            </button>
          )}
          {['quotation','proforma'].includes(billing.billingType) && billing.status === 'accepted' && (
            <button onClick={async () => {
              const due = prompt('Due date (YYYY-MM-DD)', new Date(Date.now()+15*86400000).toISOString().split('T')[0]);
              if (!due) return;
              const res = await billingService.convertToSales(id, { dueDate: due });
              toast.success('Converted!'); navigate(`/admin/billing/${res.data._id}`);
            }} className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs">
              <RefreshCw className="w-3.5 h-3.5" /> Convert to Invoice
            </button>
          )}
          <button onClick={handleDuplicate}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs">
            <Copy className="w-3.5 h-3.5" /> Duplicate
          </button>
          <Link to={`/admin/billing/${id}/edit`}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs">
            <Edit3 className="w-3.5 h-3.5" /> Edit
          </Link>
          {billing.status !== 'cancelled' && (
            <button onClick={handleCancel}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-red-50 hover:bg-red-100 text-red-600 font-bold text-xs">
              <XCircle className="w-3.5 h-3.5" /> Cancel
            </button>
          )}
          <button onClick={handlePrint} disabled={generatingPdf}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold text-xs">
            {generatingPdf ? (
              <><svg className="w-3.5 h-3.5 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/></svg> Generating...</>
            ) : (
              <><Printer className="w-3.5 h-3.5" /> Print / PDF</>
            )}
          </button>
        </div>
      </div>

      {/* GST Tax Invoice */}
      <div ref={printRef} className="print-area bg-white border border-slate-200 rounded-2xl shadow-sm p-8 sm:p-12 max-w-[210mm] mx-auto print:border-none print:shadow-none print:rounded-none font-sans text-slate-900">

        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start border-b-2 border-slate-900 pb-6 mb-6 gap-4">
          <div>
            {/* Logo */}
            <div className="flex items-center gap-3 mb-2">
              {logoUrl ? (
                <div className="relative group">
                  <img src={logoUrl} alt="Logo" className="h-14 max-w-[160px] object-contain" />
                  <div className="print:hidden absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded flex items-center justify-center gap-1">
                    <button onClick={() => logoInputRef.current?.click()} className="text-white text-[10px] font-bold bg-blue-600 px-1.5 py-0.5 rounded">Edit</button>
                    <button onClick={handleLogoRemove} className="text-white text-[10px] font-bold bg-red-600 px-1.5 py-0.5 rounded">Remove</button>
                  </div>
                </div>
              ) : (
                <div className="print:hidden">
                  <button onClick={() => !logoUploading && logoInputRef.current?.click()} disabled={logoUploading}
                    className="flex items-center gap-1.5 px-3 py-2 border-2 border-dashed border-slate-300 rounded-xl text-slate-400 hover:border-purple-400 hover:text-purple-600 transition-colors text-xs font-semibold disabled:opacity-60 disabled:cursor-not-allowed">
                    {logoUploading ? (
                      <><svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/></svg> Uploading...</>
                    ) : (
                      <><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg> Upload Logo</>
                    )}
                  </button>
                </div>
              )}
              {!logoUrl && <span className="text-2xl font-black tracking-tight text-purple-700">PRONEST</span>}
              <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
            </div>
            <p className="text-xs text-slate-600 max-w-xs font-medium">PRONEST Commercial Solutions &amp; Electronics Hub</p>
            <div className="mt-2 space-y-0.5 text-xs text-slate-700 font-medium">
              <p><strong>GSTIN:</strong> 33AAAPL1234A1Z5 &nbsp;·&nbsp; <strong>State:</strong> 33-Tamil Nadu</p>
              <p><strong>Email:</strong> sales@pronest.in &nbsp;·&nbsp; <strong>Phone:</strong> +91 9876543210</p>
            </div>
          </div>
          <div className="sm:text-right">
            <span className="inline-block px-4 py-1.5 bg-slate-900 text-white text-sm font-black tracking-widest rounded-lg uppercase mb-3">
              {docTitle}
            </span>
            <p className="font-mono font-bold text-lg text-slate-900">#{billing.billNumber || '—'}</p>
            <p className="text-xs font-medium text-slate-600 mt-0.5"><strong>Date:</strong> {fmtDate(billing.date)}</p>
            {billing.dueDate && <p className="text-xs font-bold text-red-600 mt-0.5"><strong>Due:</strong> {fmtDate(billing.dueDate)}</p>}
            {billing.validUntil && <p className="text-xs font-medium text-slate-500 mt-0.5"><strong>Valid Until:</strong> {fmtDate(billing.validUntil)}</p>}
          </div>
        </div>

        {/* Party Details */}
        <div className="grid grid-cols-2 gap-6 mb-5 text-xs">
          <div className="bg-slate-50/70 p-4 rounded-xl border border-slate-200/80">
            <span className="block text-[10px] font-black uppercase text-purple-700 tracking-wider mb-2">
              {billing.billingType === 'purchase' ? 'Seller (Vendor)' : 'Supplier'}
            </span>
            <p className="font-bold text-slate-900 text-sm">PRONEST</p>
            <p className="font-mono font-bold text-slate-700 text-[11px] mt-1">GSTIN: 33AAAPL1234A1Z5</p>
          </div>
          <div className="bg-slate-50/70 p-4 rounded-xl border border-slate-200/80">
            <span className="block text-[10px] font-black uppercase text-blue-700 tracking-wider mb-2">
              {billing.billingType === 'purchase' ? 'Recipient' : 'Billed To'}
            </span>
            <p className="font-bold text-slate-900 text-sm">{partyName}</p>
            {partyGstin ? (
              <p className="font-mono font-bold text-slate-700 text-[11px] mt-1">GSTIN: {partyGstin}</p>
            ) : (
              <p className="text-slate-400 text-[11px] mt-1 italic">Unregistered Consumer (B2C)</p>
            )}
            {billing.partyPhone && <p className="text-slate-600 mt-0.5">Ph: {billing.partyPhone}</p>}
            {billing.partyEmail && <p className="text-slate-600">{billing.partyEmail}</p>}
            {billing.billingAddress?.line1 && (
              <p className="text-slate-600 mt-0.5">{[billing.billingAddress.line1, billing.billingAddress.city, billing.billingAddress.state, billing.billingAddress.pincode].filter(Boolean).join(', ')}</p>
            )}
          </div>
        </div>

        {/* GST Info Bar */}
        <div className="flex flex-wrap items-center justify-between text-[11px] font-semibold bg-slate-100/70 px-4 py-2.5 rounded-xl border border-slate-200 mb-5 gap-2">
          <span><strong>Place of Supply:</strong> {billing.placeOfSupply || '33-Tamil Nadu'}</span>
          <span><strong>Reverse Charge:</strong> {billing.reverseCharge === 'Y' ? 'YES' : 'NO'}</span>
          <span><strong>Supply:</strong> {billing.isInterState ? 'Interstate (IGST)' : 'Intrastate (CGST+SGST)'}</span>
          <span><strong>Type:</strong> {(billing.supplyType || 'regular').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
          {billing.referenceNumber && <span><strong>Ref:</strong> {billing.referenceNumber}</span>}
        </div>

        {/* Items Table */}
        <div className="overflow-x-auto mb-6">
          <table className="w-full border-collapse border border-slate-300 text-xs">
            <thead className="bg-slate-100 text-slate-800">
              <tr>
                <th className="border border-slate-300 px-2.5 py-2 text-center font-bold uppercase tracking-wide w-8">#</th>
                <th className="border border-slate-300 px-3 py-2 text-left font-bold uppercase tracking-wide">Description</th>
                <th className="border border-slate-300 px-2.5 py-2 text-center font-bold uppercase tracking-wide">HSN/SAC</th>
                <th className="border border-slate-300 px-2.5 py-2 text-center font-bold uppercase tracking-wide">Unit</th>
                <th className="border border-slate-300 px-2.5 py-2 text-right font-bold uppercase tracking-wide">Qty</th>
                <th className="border border-slate-300 px-2.5 py-2 text-right font-bold uppercase tracking-wide">Rate</th>
                <th className="border border-slate-300 px-2.5 py-2 text-right font-bold uppercase tracking-wide">Disc%</th>
                <th className="border border-slate-300 px-2.5 py-2 text-right font-bold uppercase tracking-wide">Taxable</th>
                <th className="border border-slate-300 px-2.5 py-2 text-center font-bold uppercase tracking-wide">GST%</th>
                {!billing.isInterState ? (
                  <>
                    <th className="border border-slate-300 px-2.5 py-2 text-right font-bold uppercase tracking-wide">CGST</th>
                    <th className="border border-slate-300 px-2.5 py-2 text-right font-bold uppercase tracking-wide">SGST</th>
                  </>
                ) : (
                  <th className="border border-slate-300 px-2.5 py-2 text-right font-bold uppercase tracking-wide">IGST</th>
                )}
                <th className="border border-slate-300 px-3 py-2 text-right font-bold uppercase tracking-wide">Total</th>
              </tr>
            </thead>
            <tbody>
              {(billing.items || []).map((item, idx) => (
                <tr key={idx} className="hover:bg-slate-50/50">
                  <td className="border border-slate-300 px-2.5 py-2 text-center font-mono text-slate-500">{idx + 1}</td>
                  <td className="border border-slate-300 px-3 py-2 font-bold text-slate-900">{item.description}</td>
                  <td className="border border-slate-300 px-2.5 py-2 text-center font-mono">{item.hsnCode || item.sacCode || '—'}</td>
                  <td className="border border-slate-300 px-2.5 py-2 text-center font-semibold">{item.unit || 'Nos'}</td>
                  <td className="border border-slate-300 px-2.5 py-2 text-right font-mono font-bold">{item.quantity}</td>
                  <td className="border border-slate-300 px-2.5 py-2 text-right font-mono">₹{fmt(item.unitPrice)}</td>
                  <td className="border border-slate-300 px-2.5 py-2 text-right font-mono">{item.discount || 0}%</td>
                  <td className="border border-slate-300 px-2.5 py-2 text-right font-mono font-semibold">₹{fmt(item.taxableAmount)}</td>
                  <td className="border border-slate-300 px-2.5 py-2 text-center font-mono">{item.gstRate || 0}%</td>
                  {!billing.isInterState ? (
                    <>
                      <td className="border border-slate-300 px-2.5 py-2 text-right font-mono">₹{fmt(item.cgstAmount)}</td>
                      <td className="border border-slate-300 px-2.5 py-2 text-right font-mono">₹{fmt(item.sgstAmount)}</td>
                    </>
                  ) : (
                    <td className="border border-slate-300 px-2.5 py-2 text-right font-mono">₹{fmt(item.igstAmount)}</td>
                  )}
                  <td className="border border-slate-300 px-3 py-2 text-right font-mono font-black text-slate-900">₹{fmt(item.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totals & Words */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
          {/* Amount in words */}
          <div className="space-y-3">
            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
              <span className="block text-[10px] font-black uppercase text-slate-400 mb-1">Amount in Words</span>
              <p className="font-bold text-purple-900 italic text-sm">{numberToWords(billing.totalAmount)}</p>
            </div>
            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 text-[11px] space-y-1 text-slate-700">
              <span className="block text-[10px] font-black uppercase text-slate-400 mb-1">Bank Details</span>
              <p><strong>Bank:</strong> Kotak Mahindra </p>
              <p><strong>A/C Name:-</strong> PRONEST</p>
              <p><strong>A/C No:-</strong>4053858762</p>
              <p><strong>IFSC:</strong> KKBK0008810</p>
            </div>
          </div>

          {/* Tax breakdown */}
          <div className="space-y-1.5 text-xs font-semibold">
            <div className="flex justify-between text-slate-600 pb-1.5 border-b border-slate-200">
              <span>Taxable Value</span>
              <span className="font-mono font-bold text-slate-900">₹{fmt(billing.taxableValue || billing.subtotal)}</span>
            </div>
            {!billing.isInterState ? (
              <>
                <div className="flex justify-between text-emerald-700">
                  <span>CGST</span><span className="font-mono">₹{fmt(billing.cgstTotal)}</span>
                </div>
                <div className="flex justify-between text-teal-700">
                  <span>SGST</span><span className="font-mono">₹{fmt(billing.sgstTotal)}</span>
                </div>
              </>
            ) : (
              <div className="flex justify-between text-indigo-700">
                <span>IGST</span><span className="font-mono">₹{fmt(billing.igstTotal)}</span>
              </div>
            )}
            {(billing.cessTotal || 0) > 0 && (
              <div className="flex justify-between text-slate-600">
                <span>Cess</span><span className="font-mono">₹{fmt(billing.cessTotal)}</span>
              </div>
            )}
            <div className="flex justify-between text-slate-700 pt-1.5 border-t border-slate-200">
              <span>Total Tax</span><span className="font-mono font-bold text-slate-900">₹{fmt(billing.taxAmount)}</span>
            </div>
            {(billing.otherCharges || 0) > 0 && (
              <div className="flex justify-between text-slate-600">
                <span>{billing.otherChargesDesc || 'Other Charges'}</span>
                <span className="font-mono">₹{fmt(billing.otherCharges)}</span>
              </div>
            )}
            {billing.roundOff !== 0 && (
              <div className="flex justify-between text-slate-400 text-[10px]">
                <span>Round Off</span><span className="font-mono">{billing.roundOff > 0 ? '+' : ''}₹{fmt(billing.roundOff)}</span>
              </div>
            )}
            <div className="flex justify-between items-center pt-2 border-t-2 border-slate-900">
              <span className="font-black text-sm text-slate-900">Grand Total</span>
              <span className="font-mono font-black text-xl text-purple-700">₹{fmt(billing.totalAmount)}</span>
            </div>
            {(billing.paidAmount || 0) > 0 && (
              <div className="flex justify-between text-emerald-700 font-bold pt-1">
                <span>Paid</span><span className="font-mono">₹{fmt(billing.paidAmount)}</span>
              </div>
            )}
            {balance > 0 ? (
              <div className="flex justify-between text-red-600 font-bold pt-1 border-t border-red-100">
                <span>Balance Due</span><span className="font-mono">₹{fmt(balance)}</span>
              </div>
            ) : (billing.paidAmount || 0) >= billing.totalAmount ? (
              <div className="flex justify-between text-emerald-600 font-bold pt-1">
                <span>Status</span><span className="uppercase tracking-wider">PAID IN FULL ✓</span>
              </div>
            ) : null}
          </div>
        </div>

        {/* Terms & Signature */}
        <div className="border-t border-slate-200 pt-6 grid grid-cols-2 gap-6 text-[11px] text-slate-600">
          <div>
            <span className="block font-black text-[10px] uppercase text-slate-400 mb-1">Terms & Conditions</span>
            <p className="font-mono text-[10px] leading-relaxed whitespace-pre-line">
              {billing.terms || '1. Goods once sold will not be taken back.\n2. All disputes subject to local jurisdiction.'}
            </p>
            {billing.notes && (
              <div className="mt-3">
                <span className="block font-black text-[10px] uppercase text-slate-400 mb-1">Notes</span>
                <p className="font-medium">{billing.notes}</p>
              </div>
            )}
          </div>
          <div className="text-right flex flex-col justify-between">
            <span className="font-black text-[10px] uppercase text-slate-400">For PRONEST</span>
            <div className="mt-12 border-t border-slate-400 pt-1.5 w-48 ml-auto text-center">
              <p className="font-bold text-[10px] text-slate-700">Authorized Signatory</p>
            </div>
          </div>
        </div>

        <p className="text-center text-[9px] text-slate-300 pt-6 border-t border-slate-100 mt-6">
          Computer generated document. No signature required — GST Rules 2017.
        </p>
      </div>

      {/* Payment Modal */}
      {showPayment && (
        <PaymentModal billing={billing} onSave={() => { setShowPayment(false); fetch(); }} onClose={() => setShowPayment(false)} />
      )}
    </div>
  );
}
